import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { http, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Wrench, ShieldCheck, ExternalLink, RefreshCcw, Copy, KeyRound, AlertTriangle, HardDrive } from "lucide-react";

/**
 * §E2.9 P3 — Salon Central (TECH_ADMIN only).
 * Wizard tecnico "+ Crea Salone" con: idempotenza, tenant_id univoco,
 * stato provisioning, gate deployment manuale, riprendi, one-time credentials.
 */

const STATE_BADGE = {
  requested: "bg-neutral-200 text-neutral-800",
  validating: "bg-sky-100 text-sky-800",
  provisioning: "bg-blue-100 text-blue-800",
  credentials_ready: "bg-emerald-100 text-emerald-800",
  preview_ready: "bg-emerald-200 text-emerald-900",
  failed: "bg-rose-100 text-rose-800",
  rollback_required: "bg-orange-100 text-orange-800",
  rolled_back: "bg-neutral-200 text-neutral-700",
  manual_action_required: "bg-amber-100 text-amber-900",
};

function StateBadge({ state }) {
  const cls = STATE_BADGE[state] || "bg-neutral-100 text-neutral-800";
  return (
    <span data-testid={`state-badge-${state}`}
      className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-sm ${cls}`}>
      {state}
    </span>
  );
}

function SecretsStatusPanel() {
  const q = useQuery({
    queryKey: ["sc-secrets-status"],
    queryFn: async () => (await http.get("/saloncentral/secrets-status")).data,
    refetchOnWindowFocus: false,
  });
  const preflight = useQuery({
    queryKey: ["sc-preflight-b2"],
    queryFn: async () => (await http.get("/saloncentral/preflight/backblaze")).data,
    refetchOnWindowFocus: false,
  });
  const usage = useQuery({
    queryKey: ["sc-salon-central-usage"],
    queryFn: async () => (await http.get("/saloncentral/central/usage")).data,
    refetchOnWindowFocus: false,
  });
  const d = q.data || {};
  const keys = ["E29_MASTER_ENCRYPTION_KEY", "B2_MASTER_KEY_ID", "B2_MASTER_APPLICATION_KEY"];
  return (
    <div className="card-clean p-6 mb-6" data-testid="sc-secrets-status-card">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-5 h-5 text-[var(--accent-green)]" />
        <h2 className="font-serif text-2xl">Master Secrets &amp; Preflight</h2>
      </div>
      <p className="text-xs text-neutral-500 mb-4 max-w-3xl">
        Verifica presenza/formato/lunghezza dei 3 secret. Nessun valore viene mai esposto. Il preflight Backblaze
        autentica la master key e verifica le capability richieste.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-xs">
        {keys.map(k => {
          const v = d[k] || {};
          const ok = v.present && v.format_ok && v.length_ok;
          return (
            <div key={k} className="border border-[var(--border-silver)] p-3" data-testid={`sc-secret-${k}`}>
              <div className="font-mono text-[10px] break-all">{k}</div>
              <div className="mt-2 flex flex-col gap-1">
                <span>present: <b>{String(v.present)}</b></span>
                <span>format_ok: <b>{String(v.format_ok)}</b></span>
                <span>length_ok: <b>{String(v.length_ok)}</b></span>
                {"decoded_32_bytes" in v && <span>decoded_32_bytes: <b>{String(v.decoded_32_bytes)}</b></span>}
              </div>
              <div className={`mt-2 px-2 py-0.5 inline-block ${ok ? "bg-emerald-100 text-emerald-900" : "bg-rose-100 text-rose-900"}`}>
                {ok ? "OK" : "KO"}
              </div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div className="border border-[var(--border-silver)] p-3" data-testid="sc-preflight-b2">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4" />
            <b>Backblaze preflight</b>
          </div>
          <div>ok: <b>{String(preflight.data?.ok)}</b></div>
          <div>bucket_visible: <b>{String(preflight.data?.bucket_visible)}</b></div>
          <div>capabilities_missing: <b>{(preflight.data?.capabilities_missing || []).join(", ") || "—"}</b></div>
        </div>
        <div className="border border-[var(--border-silver)] p-3" data-testid="sc-salon-central-usage">
          <div className="flex items-center gap-2 mb-1">
            <HardDrive className="w-4 h-4" />
            <b>Provisioner — uso backup</b>
          </div>
          <div>used_MiB: <b>{usage.data?.used_MiB ?? "—"}</b></div>
          <div>limit_MiB: <b>{usage.data?.limit_MiB ?? "—"}</b></div>
          <div>usage_ratio: <b>{usage.data?.usage_ratio ?? "—"}</b></div>
        </div>
      </div>
    </div>
  );
}

function slugify(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
}

function newIdempotencyKey() {
  return crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()) + "-" + Math.random().toString(36).slice(2, 10);
}

function CreateSalonWizard({ onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    public_name: "",
    slug: "",
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    environment: "preview",
    is_demo: false,
    plan: "basic",
  });
  const [idk, setIdk] = useState(newIdempotencyKey());

  const patch = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const createMut = useMutation({
    mutationFn: async () => (await http.post("/saloncentral/salons", form, {
      headers: { "Idempotency-Key": idk },
    })).data,
    onSuccess: (data) => {
      toast.success(`Salone creato — tenant_id ${data.tenant_id} (stato: ${data.provisioning_state})`);
      onCreated?.(data);
    },
    onError: (e) => toast.error(formatErr(e)),
  });

  return (
    <div className="card-clean p-6 mb-6" data-testid="sc-create-wizard">
      <div className="flex items-center gap-2 mb-4">
        <Wrench className="w-5 h-5 text-[var(--accent-green)]" />
        <h2 className="font-serif text-2xl">+ Crea Salone</h2>
      </div>
      <p className="text-xs text-neutral-500 mb-4 max-w-3xl">
        Wizard tecnico. La creazione è idempotente su Idempotency-Key: ripetere la stessa richiesta ritorna
        lo stesso salone. Slug univoco. Ogni salone riceve tenant_id, chiave Backblaze figlia con prefix
        <code className="mx-1 font-mono text-[10px]">tenants/&lt;tenant_id&gt;/</code>, envelope encryption
        della Backup Encryption Key con la master key.
      </p>
      <div className="text-[11px] text-neutral-500 mb-4">
        <b>Step {step}/3</b> — <span>Idempotency-Key:</span>{" "}
        <code className="font-mono">{idk.slice(0, 12)}…</code>
        <button className="ml-2 underline" data-testid="sc-new-idk"
          onClick={() => setIdk(newIdempotencyKey())}>rigenera</button>
      </div>
      {step === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Nome pubblico</span>
            <input value={form.public_name} onChange={e => { patch("public_name", e.target.value); patch("slug", slugify(e.target.value)); }}
              className="w-full px-3 py-2 border border-[var(--border-silver)]" data-testid="sc-input-public_name" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Slug (a-z0-9-)</span>
            <input value={form.slug} onChange={e => patch("slug", slugify(e.target.value))}
              className="w-full px-3 py-2 border border-[var(--border-silver)] font-mono text-xs" data-testid="sc-input-slug" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Nome OWNER</span>
            <input value={form.owner_name} onChange={e => patch("owner_name", e.target.value)}
              className="w-full px-3 py-2 border border-[var(--border-silver)]" data-testid="sc-input-owner_name" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Email OWNER</span>
            <input value={form.owner_email} onChange={e => patch("owner_email", e.target.value)}
              type="email" className="w-full px-3 py-2 border border-[var(--border-silver)]" data-testid="sc-input-owner_email" />
          </label>
        </div>
      )}
      {step === 2 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Ambiente</span>
            <select value={form.environment} onChange={e => patch("environment", e.target.value)}
              className="w-full px-3 py-2 border border-[var(--border-silver)]" data-testid="sc-input-environment">
              <option value="preview">preview</option>
              <option value="production">production</option>
              <option value="demo">demo</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Piano</span>
            <select value={form.plan} onChange={e => patch("plan", e.target.value)}
              className="w-full px-3 py-2 border border-[var(--border-silver)]" data-testid="sc-input-plan">
              <option value="basic">basic</option>
              <option value="standard">standard</option>
              <option value="pro">pro</option>
            </select>
          </label>
          <label className="flex items-center gap-2 md:col-span-2 text-xs">
            <input type="checkbox" checked={form.is_demo} onChange={e => patch("is_demo", e.target.checked)} data-testid="sc-input-is_demo" />
            <span>Tenant DEMO (dati fittizi, email marker <code>[DEMO]</code>, rate-limit anti-abuso)</span>
          </label>
        </div>
      )}
      {step === 3 && (
        <div className="text-sm">
          <p className="mb-2 text-neutral-700">Riepilogo — conferma per creare:</p>
          <pre className="text-[11px] p-3 bg-neutral-50 border border-[var(--border-silver)] overflow-auto">
{JSON.stringify(form, null, 2)}
          </pre>
        </div>
      )}
      <div className="flex justify-between mt-6 gap-2">
        <button className="px-3 py-2 text-sm border" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1} data-testid="sc-prev-step">Indietro</button>
        {step < 3 ? (
          <button className="px-3 py-2 text-sm bg-neutral-900 text-white" onClick={() => setStep(s => s + 1)} data-testid="sc-next-step">Avanti</button>
        ) : (
          <button className="px-3 py-2 text-sm bg-emerald-700 text-white disabled:opacity-50"
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending || !form.public_name || !form.owner_email || !form.slug}
            data-testid="sc-create-submit">
            {createMut.isPending ? "Creazione…" : "Crea salone"}
          </button>
        )}
      </div>
    </div>
  );
}

function SalonRow({ s, onRefresh }) {
  const qc = useQueryClient();
  const [depUrl, setDepUrl] = useState(s.deployment_url || "");
  const [busy, setBusy] = useState(false);

  const setDeployment = useMutation({
    mutationFn: async () => (await http.post(`/saloncentral/salons/${s.tenant_id}/deployment`, {
      deployment_url: depUrl.trim(),
    })).data,
    onSuccess: () => { toast.success("Deployment URL impostato"); onRefresh?.(); },
    onError: (e) => toast.error(formatErr(e)),
  });
  const resume = useMutation({
    mutationFn: async () => (await http.post(`/saloncentral/salons/${s.tenant_id}/resume`)).data,
    onSuccess: () => { toast.success("Provisioning ripreso"); onRefresh?.(); },
    onError: (e) => toast.error(formatErr(e)),
  });
  const reset = useMutation({
    mutationFn: async () => (await http.post(`/saloncentral/salons/${s.tenant_id}/demo/reset`)).data,
    onSuccess: () => { toast.success("Tenant DEMO resettato"); onRefresh?.(); },
    onError: (e) => toast.error(formatErr(e)),
  });
  const prodPrepare = useMutation({
    mutationFn: async () => (await http.post(`/saloncentral/salons/${s.tenant_id}/production/prepare`)).data,
    onSuccess: (d) => {
      toast.success(d.idempotent
        ? "Production già preparato — nessuna azione duplicata"
        : "Production preparato (chiave + BEK + prefisso disgiunto)");
      onRefresh?.();
    },
    onError: (e) => toast.error(formatErr(e)),
  });
  const prodView = useMutation({
    mutationFn: async () => (await http.get(`/saloncentral/salons/${s.tenant_id}/production/credentials-once`)).data,
    onSuccess: async (data) => {
      const txt = JSON.stringify(data, null, 2);
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(txt);
          copied = true;
        }
      } catch { copied = false; }
      window.prompt(
        (copied ? "✓ Copiato negli appunti. " : "") +
        "Credenziali PRODUCTION one-time (Cache-Control: no-store).\n" +
        "Salva in una voce Bitwarden DISTINTA dal Preview.\n" +
        "Mapping: secrets_mapping → Secrets Production Emergent.\n" +
        "Poi clicca 'Ho salvato in Bitwarden Production ✓'.",
        txt
      );
      toast.warning("Bundle PRODUCTION mostrato UNA SOLA VOLTA. Ora conferma salvataggio.");
      onRefresh?.();
    },
    onError: (e) => toast.error(formatErr(e)),
  });
  const prodConfirm = useMutation({
    mutationFn: async () => (await http.post(`/saloncentral/salons/${s.tenant_id}/production/confirm-bitwarden`)).data,
    onSuccess: () => { toast.success("Bitwarden Production confermato"); onRefresh?.(); },
    onError: (e) => toast.error(formatErr(e)),
  });
  // §E2.9 P5 — Reveal ONE-TIME. NON conferma automaticamente Bitwarden:
  // il salvataggio è confermato solo da un click esplicito successivo,
  // così se l'utente chiude senza salvare, il bottone "Conferma Bitwarden"
  // resta visibile e ricorda che il bundle è già stato consumato.
  const viewCreds = useMutation({
    mutationFn: async () => (await http.get(`/saloncentral/salons/${s.tenant_id}/credentials-once`)).data,
    onSuccess: async (data) => {
      const txt = JSON.stringify(data, null, 2);
      // §P5+ Copy-to-clipboard automatico (fallback a prompt se API non disponibile).
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(txt);
          copied = true;
        }
      } catch { copied = false; }
      window.prompt(
        (copied ? "✓ Copiato negli appunti. " : "") +
        "Credenziali Preview one-time (Cache-Control: no-store). Incollale in Bitwarden (voce PREVIEW).\n" +
        "Mapping: secrets_mapping.{BACKUP_S3_*, BACKUP_ENC_KEY, WEBHOOK_CRON_SECRET} → Secrets Preview Emergent.\n" +
        "Poi clicca 'Ho salvato in Bitwarden ✓'.",
        txt
      );
      toast.warning("Bundle PREVIEW mostrato UNA SOLA VOLTA. Ora conferma salvataggio.");
      onRefresh?.();
    },
    onError: (e) => toast.error(formatErr(e)),
  });
  const confirmBw = useMutation({
    mutationFn: async () => (await http.post(`/saloncentral/salons/${s.tenant_id}/confirm-bitwarden`)).data,
    onSuccess: () => { toast.success("Salvataggio Bitwarden confermato"); onRefresh?.(); },
    onError: (e) => toast.error(formatErr(e)),
  });

  return (
    <tr className="border-b" data-testid={`sc-row-${s.tenant_id}`}>
      <td className="p-2 font-mono text-[11px]">{s.tenant_id}</td>
      <td className="p-2">{s.public_name}</td>
      <td className="p-2 font-mono text-xs">{s.slug}</td>
      <td className="p-2"><StateBadge state={s.provisioning_state} /></td>
      <td className="p-2 text-[11px]">{s.environment}{s.is_demo && <span className="ml-1 px-1 bg-amber-200">DEMO</span>}</td>
      <td className="p-2 text-[10px] font-mono" title="Runtime template pinnato">
        {s.runtime_template ? (
          <span className="px-1 bg-emerald-50 text-emerald-900 border border-emerald-200" data-testid={`sc-runtime-${s.tenant_id}`}>
            {s.runtime_template}
          </span>
        ) : (
          <span className="text-neutral-400">—</span>
        )}
      </td>
      <td className="p-2">
        {s.provisioning_state === "manual_action_required" && (
          <div className="flex flex-col gap-1">
            <input placeholder="URL/ID deployment" className="px-2 py-1 border text-[11px] w-56"
              value={depUrl} onChange={e => setDepUrl(e.target.value)} data-testid={`sc-dep-input-${s.tenant_id}`} />
            <div className="flex gap-1">
              <button className="text-[11px] px-2 py-0.5 border" data-testid={`sc-dep-save-${s.tenant_id}`}
                onClick={() => setDeployment.mutate()} disabled={!depUrl}>Salva URL</button>
              <button className="text-[11px] px-2 py-0.5 bg-neutral-900 text-white" data-testid={`sc-resume-${s.tenant_id}`}
                onClick={() => resume.mutate()}>
                <RefreshCcw className="inline w-3 h-3 mr-1" />Riprendi
              </button>
            </div>
          </div>
        )}
        {/* §P5++ Reveal Preview disponibile FINO alla conferma Bitwarden (ripresa senza rigenerazione) */}
        {["credentials_ready", "manual_action_required", "preview_ready"].includes(s.provisioning_state)
          && !s.bitwarden_confirmed_at && (
          <button className="text-[11px] px-2 py-0.5 border bg-amber-50" data-testid={`sc-view-creds-${s.tenant_id}`}
            onClick={() => viewCreds.mutate()}>
            {s.credentials_bundle_viewed ? "Rivedi credenziali" : "Mostra credenziali (una volta)"}
          </button>
        )}
        {["credentials_ready", "manual_action_required", "preview_ready"].includes(s.provisioning_state)
          && s.credentials_bundle_viewed && !s.bitwarden_confirmed_at && (
          <button className="text-[11px] px-2 py-0.5 border bg-emerald-50 ml-1"
            data-testid={`sc-confirm-bw-${s.tenant_id}`}
            onClick={() => confirmBw.mutate()}>
            Ho salvato in Bitwarden ✓
          </button>
        )}
        {s.is_demo && (
          <button className="text-[11px] px-2 py-0.5 border ml-1" data-testid={`sc-reset-${s.tenant_id}`}
            onClick={() => { if (window.confirm("Reset del tenant DEMO. Continuare?")) reset.mutate(); }}>
            Reset DEMO
          </button>
        )}
        {/* §P5++ Gate Vai in Production: solo preview_ready + Bitwarden Preview confermato + non-demo + non già preparato */}
        {!s.is_demo
          && s.provisioning_state === "preview_ready"
          && !!s.bitwarden_confirmed_at
          && !s.prod_b2_key_id && (
          <div className="mt-1 border-t pt-1">
            <button className="text-[11px] px-2 py-0.5 border bg-indigo-50 text-indigo-900"
              data-testid={`sc-prod-prepare-${s.tenant_id}`}
              onClick={() => {
                if (window.confirm(
                  "VAI IN PRODUCTION\n\n" +
                  "Prepara credenziali Backblaze + BEK + WEBHOOK_CRON_SECRET Production dedicate\n" +
                  "(prefisso disgiunto da Preview, isolamento verificato).\n\n" +
                  "NON pubblica il salone, NON ruota le chiavi Preview esistenti.\n" +
                  "Dopo la preparazione il badge sarà 'PROD READY' = configurazione Production pronta.\n\n" +
                  "Procedere?"
                )) prodPrepare.mutate();
              }}>
              Vai in Production
            </button>
            <div className="text-[9px] text-neutral-500 mt-1">
              Configurazione Production — non esegue deploy.
            </div>
          </div>
        )}
        {/* Reveal bundle Production — disponibile FINO alla conferma Bitwarden Production */}
        {!s.is_demo && s.prod_b2_key_id && !s.prod_bitwarden_confirmed_at && (
          <button className="mt-1 text-[11px] px-2 py-0.5 border bg-indigo-50 text-indigo-900"
            data-testid={`sc-prod-view-${s.tenant_id}`}
            onClick={() => prodView.mutate()}>
            {s.prod_credentials_bundle_viewed ? "Rivedi credenziali Production" : "Mostra credenziali Production (una volta)"}
          </button>
        )}
        {!s.is_demo && s.prod_b2_key_id && s.prod_credentials_bundle_viewed && !s.prod_bitwarden_confirmed_at && (
          <button className="mt-1 text-[11px] px-2 py-0.5 border bg-emerald-50 text-emerald-900"
            data-testid={`sc-prod-confirm-bw-${s.tenant_id}`}
            onClick={() => prodConfirm.mutate()}>
            Ho salvato in Bitwarden Production ✓
          </button>
        )}
        {!s.is_demo && s.prod_bitwarden_confirmed_at && (
          <div className="mt-1 text-[10px] px-1 inline-block bg-indigo-100 text-indigo-900"
               data-testid={`sc-prod-ready-${s.tenant_id}`}
               title={`Configurazione Production pronta — prefix: ${s.prod_b2_prefix}`}>
            PROD READY — configurazione pronta
          </div>
        )}
      </td>
    </tr>
  );
}

function RuntimePanel() {
  const q = useQuery({
    queryKey: ["sc-runtime-manifest"],
    queryFn: async () => (await http.get("/saloncentral/runtime/manifest")).data,
    refetchOnWindowFocus: false,
  });
  const d = q.data || {};
  return (
    <div className="card-clean p-6 mb-6" data-testid="sc-runtime-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-[var(--accent-green)]" />
        <h2 className="font-serif text-2xl">Runtime template ufficiale</h2>
      </div>
      <p className="text-xs text-neutral-500 mb-3 max-w-3xl">
        Pacchetto runtime installabile e immutabile che verrà usato per ogni nuovo salone.
        Distinto dal <b>template documentale</b> (manifest JSON). "+ Crea Salone" pinna
        automaticamente questa versione sul tenant senza mai copiare il codice vivo di
        questo pod (baseline Salon Central).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div className="border border-[var(--border-silver)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Version pinnata</div>
          <div className="mt-1 font-mono text-emerald-800" data-testid="sc-runtime-version">{d.runtime_version || "—"}</div>
          <div className="mt-1 text-[10px] text-neutral-500">immutable: <b>{String(d.immutable ?? "—")}</b></div>
        </div>
        <div className="border border-[var(--border-silver)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Pacchetto disponibile</div>
          <div className="mt-1">exists: <b data-testid="sc-runtime-exists">{String(d.exists ?? "—")}</b></div>
          <div className="mt-1 text-[10px]">
            backend: <b>{d.backend_file_count ?? "—"}</b> · frontend: <b>{d.frontend_file_count ?? "—"}</b> · migrations: <b>{d.migrations_count ?? "—"}</b>
          </div>
        </div>
        <div className="border border-[var(--border-silver)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Integrità</div>
          <div className="mt-1">version_file_matches: <b>{String(d.version_file_matches ?? "—")}</b></div>
          <div className="mt-1 font-mono text-[10px]">checksum_sha256_16: {d.checksum_file_sha256_short || "—"}</div>
        </div>
      </div>
    </div>
  );
}

export default function SalonCentral() {
  const listQ = useQuery({
    queryKey: ["sc-salons"],
    queryFn: async () => (await http.get("/saloncentral/salons")).data,
    refetchOnWindowFocus: false,
  });

  const refresh = () => listQ.refetch();

  return (
    <div className="p-4 md:p-8" data-testid="salon-central-page">
      <div className="mb-4">
        <h1 className="font-serif text-3xl md:text-4xl">Salon Central</h1>
        <p className="text-xs text-neutral-500 mt-1 max-w-3xl">
          Motore di provisioning multi-tenant (§E2.9). Non tocca i dati di Salon Central. Ogni salone riceve chiave
          Backblaze figlia con prefix tenants/&lt;tenant_id&gt;/, Backup Encryption Key cifrata via envelope.
        </p>
      </div>

      <SecretsStatusPanel />
      <RuntimePanel />
      <CreateSalonWizard onCreated={refresh} />

      <div className="card-clean p-6" data-testid="sc-salons-list-card">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-[var(--accent-green)]" />
          <h2 className="font-serif text-2xl">Saloni</h2>
          <button className="ml-auto text-xs underline" onClick={refresh} data-testid="sc-list-refresh">
            <RefreshCcw className="inline w-3 h-3 mr-1" />Aggiorna
          </button>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-xs" data-testid="sc-salons-table">
            <thead>
              <tr className="border-b bg-neutral-50">
                <th className="p-2 text-left">tenant_id</th>
                <th className="p-2 text-left">Nome</th>
                <th className="p-2 text-left">Slug</th>
                <th className="p-2 text-left">Stato provisioning</th>
                <th className="p-2 text-left">Env</th>
                <th className="p-2 text-left">Runtime</th>
                <th className="p-2 text-left">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {(listQ.data || []).map(s => (
                <SalonRow key={s.tenant_id} s={s} onRefresh={refresh} />
              ))}
              {(listQ.data || []).length === 0 && !listQ.isLoading && (
                <tr><td colSpan="7" className="p-4 text-center text-neutral-500">Nessun salone. Usa "+ Crea Salone" sopra.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
